// Google Analytics utility functions for tracking events and errors

// Check if GA is available and should track events
const isGAAvailable = (): boolean => {
    return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Base event tracking function
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
    if (isGAAvailable()) {
        window.gtag('event', eventName, params || {});
        console.log(`GA Event: ${eventName}`, params);
    }
};

// Track DAT creation step events
export const trackDATStepEvent = (
    stepId: string,
    status: 'start' | 'success' | 'error',
    additionalParams?: Record<string, any>,
) => {
    const eventName = `dat_step_${status}`;
    const params = {
        step_id: stepId,
        step_name: stepId.replace(/-/g, '_'),
        ...additionalParams,
    };

    trackEvent(eventName, params);
};

// Track errors with detailed information
export const trackError = (
    errorType: string,
    errorMessage: string,
    stepId?: string,
    additionalParams?: Record<string, any>,
) => {
    const params = {
        error_type: errorType,
        error_message: errorMessage,
        step_id: stepId || 'unknown',
        timestamp: new Date().toISOString(),
        ...additionalParams,
    };

    trackEvent('error_occurred', params);

    // Also send to GA as an exception
    if (isGAAvailable()) {
        window.gtag('event', 'exception', {
            description: `${errorType}: ${errorMessage}`,
            fatal: false,
            custom_map: {
                step_id: stepId,
            },
        });
    }
};

// Track user actions
export const trackUserAction = (
    action: string,
    category: string = 'user_interaction',
    additionalParams?: Record<string, any>,
) => {
    const params = {
        event_category: category,
        event_label: action,
        ...additionalParams,
    };

    trackEvent('user_action', params);
};

// Track transaction events
export const trackTransaction = (
    transactionType: string,
    status: 'initiated' | 'pending' | 'success' | 'failed',
    additionalParams?: Record<string, any>,
) => {
    const eventName = `transaction_${status}`;
    const params = {
        transaction_type: transactionType,
        ...additionalParams,
    };

    trackEvent(eventName, params);
};

// Track API calls
export const trackAPICall = (
    apiName: string,
    status: 'start' | 'success' | 'error',
    responseTime?: number,
    additionalParams?: Record<string, any>,
) => {
    const eventName = `api_${status}`;
    const params = {
        api_name: apiName,
        response_time: responseTime,
        ...additionalParams,
    };

    trackEvent(eventName, params);
};

// Track file operations
export const trackFileOperation = (
    operation: 'upload' | 'encrypt' | 'download',
    status: 'start' | 'success' | 'error',
    fileSize?: number,
    additionalParams?: Record<string, any>,
) => {
    const eventName = `file_${operation}_${status}`;
    const params = {
        file_size: fileSize,
        ...additionalParams,
    };

    trackEvent(eventName, params);
};

// Track blockchain operations
export const trackBlockchainOperation = (
    operation: string,
    contractName: string,
    status: 'start' | 'success' | 'error',
    gasUsed?: string,
    additionalParams?: Record<string, any>,
) => {
    const eventName = `blockchain_${status}`;
    const params = {
        operation,
        contract_name: contractName,
        gas_used: gasUsed,
        ...additionalParams,
    };

    trackEvent(eventName, params);
};
